import React from 'react';
import cn from 'classnames';
import { useAdBlockGate } from 'src/context/AdBlockGateContext';
import { useDFP } from 'src/hooks/dfp';
import { EMPTY_BANNER_PARAMS, getBannerParams, shouldServeAds } from 'src/utils/banner-params';
import { detectAdBlock } from 'src/utils/detectAdBlock';

import css from './Banner.module.scss';

type Props = {
  /** Fires when results content may start its entrance animations (ad closed or empty slot). */
  onContentGateOpen?: () => void;
};

function Banner({ onContentGateOpen }: Props) {
  const { reportAdBlockDetected } = useAdBlockGate();
  const adsEnabled = shouldServeAds();
  const [params, setParams] = React.useState(EMPTY_BANNER_PARAMS);
  const dfp = useDFP(params);
  const bannerLabel = 'פרסומת';
  const gateOpenedRef = React.useRef(false);
  const adFailureHandledRef = React.useRef(false);

  const openContentGate = React.useCallback(() => {
    if (gateOpenedRef.current) {
      return;
    }
    gateOpenedRef.current = true;
    onContentGateOpen?.();
  }, [onContentGateOpen]);

  React.useEffect(() => {
    if (!adsEnabled) {
      openContentGate();
      return;
    }

    setParams(getBannerParams());
  }, [adsEnabled, openContentGate]);

  const onCloseBtnClick = () => {
    dfp.close();
  };

  const handleAdFlowFailure = React.useCallback(async () => {
    if (adFailureHandledRef.current) {
      return;
    }

    adFailureHandledRef.current = true;

    try {
      const detection = await detectAdBlock();

      if (detection.blocked) {
        reportAdBlockDetected();
        return;
      }

      openContentGate();
    } catch (error) {
      console.error('Ad flow failure handling failed:', error);
      openContentGate();
    }
  }, [openContentGate, reportAdBlockDetected]);

  React.useEffect(() => {
    if (dfp.initStatus !== 'timeout' && dfp.initStatus !== 'failed') {
      return;
    }

    void handleAdFlowFailure();
  }, [dfp.initStatus, handleAdFlowFailure]);

  React.useEffect(() => {
    if (dfp.isClosed) {
      openContentGate();
      return;
    }

    if (dfp.slotResolved && !dfp.isRendered) {
      void handleAdFlowFailure();
    }
  }, [dfp.isClosed, dfp.isRendered, dfp.slotResolved, openContentGate, handleAdFlowFailure]);

  if (!adsEnabled || dfp.isClosed || !params.slotPath || (dfp.slotResolved && !dfp.isRendered)) {
    return null;
  }

  return (
    <div className={cn(css.bannerWrap, dfp.isRendered && css.rendered)}>
      <p className={css.label}>{bannerLabel}</p>
      <div className={css.banner}>
        <div id={params.containerId} style={{ minWidth: `${dfp.minWidth}px`, minHeight: `${dfp.minHeight}px` }} />
      </div>
      <button type="button" className={css.closeBtn} onClick={onCloseBtnClick} aria-label="סגור פרסומת" />
    </div>
  );
}

export default Banner;
